import { createFileRoute, Link } from '@tanstack/react-router';
import {
  ActionIcon,
  Anchor,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Checkbox,
  Drawer,
  Group,
  Modal,
  Pagination,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Table,
  Tabs,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { useDebouncedValue, useDisclosure, useLocalStorage } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconFile,
  IconFiles,
  IconFileText,
  IconLayoutGrid,
  IconList,
  IconMusic,
  IconPhoto,
  IconPlayerPlayFilled,
  IconSearch,
  IconTrash,
  IconUpload,
  IconVideo,
  IconX,
} from '@tabler/icons-react';
import { formatBytes } from '@leadsync/shared-utils';
import { motion } from 'framer-motion';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { useTabParam } from '../../lib/useTabParam';
import { MediaBubble } from '../../components/inbox/bubbles';
import { DocThumb } from '../../components/media/DocThumb';
import { PermissionGate } from '../../components/PermissionGate';

const PAGE_SIZE = 40;
const MAX_BYTES = 30 * 1024 * 1024;

type MediaKind = 'image' | 'video' | 'audio' | 'document' | 'sticker' | 'other';

type MediaItem = {
  id: number;
  uid: string;
  kind: string;
  mimeType: string | null;
  fileSize: number | null;
  title: string | null;
  fileName: string | null;
  source: string;
  channel: string;
  direction: string;
  createdAt: string | Date;
  mediaUrl: string | null;
};

const MEDIA_TYPES = ['all', 'images', 'videos', 'documents', 'audio', 'other'] as const;
type MediaType = (typeof MEDIA_TYPES)[number];

/** Active tab → the kinds passed to `media.list`. Images folds stickers in; All
 *  sends nothing (no kind filter). */
const TYPE_KINDS: Record<MediaType, MediaKind[] | undefined> = {
  all: undefined,
  images: ['image', 'sticker'],
  videos: ['video'],
  documents: ['document'],
  audio: ['audio'],
  other: ['other'],
};

const TAB_META: { value: MediaType; label: string; Icon: typeof IconPhoto }[] = [
  { value: 'all', label: 'All', Icon: IconFiles },
  { value: 'images', label: 'Images', Icon: IconPhoto },
  { value: 'videos', label: 'Videos', Icon: IconVideo },
  { value: 'documents', label: 'Documents', Icon: IconFileText },
  { value: 'audio', label: 'Audio', Icon: IconMusic },
  { value: 'other', label: 'Other', Icon: IconFile },
];

/** Kinds that get a visual preview and are worth stepping through in the drawer. */
const NAV_KINDS = new Set(['image', 'video', 'sticker']);

const CHANNEL_OPTIONS = [
  { value: '', label: 'All sources' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'messenger', label: 'Messenger' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'none', label: 'Library / flow' },
];

const SORT_OPTIONS = [
  { value: 'createdAt:desc', label: 'Newest first' },
  { value: 'createdAt:asc', label: 'Oldest first' },
  { value: 'fileSize:desc', label: 'Largest first' },
  { value: 'fileSize:asc', label: 'Smallest first' },
  { value: 'title:asc', label: 'Name A–Z' },
];

const INBOX_PATH: Record<string, string> = {
  whatsapp: '/vendor/chat',
  messenger: '/vendor/facebook/inbox',
  instagram: '/vendor/instagram/inbox',
};

type UsageData = { totalCount: number; byKind: { kind: string; count: number }[] };

/** Per-tab item count from the usage meter (Images = image + sticker). */
function countForType(type: MediaType, usage: UsageData | undefined): number | null {
  if (!usage) return null;
  if (type === 'all') return usage.totalCount;
  const get = (k: string) => usage.byKind.find((b) => b.kind === k)?.count ?? 0;
  switch (type) {
    case 'images':
      return get('image') + get('sticker');
    case 'videos':
      return get('video');
    case 'documents':
      return get('document');
    case 'audio':
      return get('audio');
    case 'other':
      return get('other');
    default:
      return null;
  }
}

/** MediaBubble only knows image/video/audio/file — map the asset kinds onto it. */
function toBubbleKind(kind: string): 'image' | 'video' | 'audio' | 'file' {
  if (kind === 'image' || kind === 'sticker') return 'image';
  if (kind === 'video') return 'video';
  if (kind === 'audio') return 'audio';
  return 'file';
}

function sourceBadge(item: MediaItem): { label: string; color: string } {
  if (item.source === 'library_upload') return { label: 'Library', color: 'grape' };
  if (item.source === 'flow') return { label: 'Flow', color: 'cyan' };
  if (item.channel === 'whatsapp') return { label: 'WhatsApp', color: 'green' };
  if (item.channel === 'messenger') return { label: 'Messenger', color: 'blue' };
  if (item.channel === 'instagram') return { label: 'Instagram', color: 'pink' };
  return { label: item.source, color: 'gray' };
}

function displayName(item: MediaItem): string {
  return item.title || item.fileName || `${item.kind} · ${item.uid.slice(0, 8)}`;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/** Editable name: shows text, swaps to an input on double-click, commits on
 *  Enter/blur, cancels on Escape. Stops click propagation so renaming never
 *  also opens the detail drawer. */
function EditableName({
  item,
  onRename,
}: {
  item: MediaItem;
  onRename: (id: number, title: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');

  if (editing) {
    const commit = () => {
      const v = value.trim();
      if (v && v !== displayName(item)) onRename(item.id, v);
      setEditing(false);
    };
    return (
      <TextInput
        size="xs"
        data-autofocus
        autoFocus
        value={value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => setValue(e.currentTarget.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Escape') {
            e.preventDefault();
            setEditing(false);
          }
        }}
      />
    );
  }
  return (
    <Text
      size="sm"
      fw={500}
      truncate
      title={`${displayName(item)} — double-click to rename`}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => {
        e.stopPropagation();
        setValue(displayName(item));
        setEditing(true);
      }}
    >
      {displayName(item)}
    </Text>
  );
}

/** Square preview for a grid tile — image thumbnail, a seeked poster frame +
 *  play badge for video, a rendered first page for documents, and a labeled
 *  icon tile for audio/other. */
function Thumb({ item }: { item: MediaItem }) {
  const [broken, setBroken] = useState(false);
  const kind = item.kind;
  const url = item.mediaUrl;
  const ext = (item.fileName?.split('.').pop() || item.mimeType?.split('/').pop() || '')
    .toUpperCase()
    .slice(0, 4);

  if ((kind === 'image' || kind === 'sticker') && url && !broken) {
    return (
      <img
        src={url}
        alt={displayName(item)}
        onError={() => setBroken(true)}
        style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }}
      />
    );
  }
  if (kind === 'video' && url && !broken) {
    return (
      <Box style={{ position: 'relative', height: 130, background: '#000' }}>
        <video
          src={`${url}#t=0.1`}
          muted
          preload="metadata"
          onError={() => setBroken(true)}
          style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }}
        />
        <Center style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          <ThemeIcon radius="xl" size="lg" variant="filled" color="dark" style={{ opacity: 0.85 }}>
            <IconPlayerPlayFilled size={18} />
          </ThemeIcon>
        </Center>
      </Box>
    );
  }
  if (kind === 'document') {
    return <DocThumb item={item} />;
  }
  const Icon = kind === 'audio' ? IconMusic : kind === 'video' ? IconVideo : IconFile;
  return (
    <Stack
      align="center"
      justify="center"
      gap={4}
      style={{
        height: 130,
        background: 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
      }}
    >
      <Icon size={36} opacity={0.55} />
      <Text size="xs" c="dimmed" fw={600}>
        {ext || (kind === 'audio' ? 'AUDIO' : kind === 'video' ? 'VIDEO' : 'FILE')}
      </Text>
    </Stack>
  );
}

function MediaTile({
  item,
  selected,
  onToggle,
  onOpen,
  onRename,
}: {
  item: MediaItem;
  selected: boolean;
  onToggle: () => void;
  onOpen: () => void;
  onRename: (id: number, title: string) => void;
}) {
  const badge = sourceBadge(item);
  return (
    <Card padding={0} radius="md" withBorder style={{ overflow: 'hidden', position: 'relative' }}>
      <Box style={{ position: 'absolute', top: 6, left: 6, zIndex: 2 }}>
        <Checkbox
          checked={selected}
          onChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          aria-label="Select"
        />
      </Box>
      <Box style={{ cursor: 'pointer' }} onClick={onOpen}>
        <Thumb item={item} />
        <Stack gap={2} p="xs">
          <EditableName item={item} onRename={onRename} />
          <Group justify="space-between" gap={4}>
            <Badge size="xs" variant="light" color={badge.color}>
              {badge.label}
            </Badge>
            <Text size="xs" c="dimmed">
              {formatBytes(item.fileSize)}
            </Text>
          </Group>
        </Stack>
      </Box>
    </Card>
  );
}

function DetailDrawer({
  item,
  navItems,
  onNavigate,
  onClose,
  onRename,
  onDelete,
}: {
  item: MediaItem | null;
  navItems: MediaItem[];
  onNavigate: (item: MediaItem) => void;
  onClose: () => void;
  onRename: (id: number, title: string) => void;
  onDelete: (item: MediaItem) => void;
}) {
  const [title, setTitle] = useState('');
  useEffect(() => {
    if (item) setTitle(displayName(item));
  }, [item]);

  const navIndex = item ? navItems.findIndex((i) => i.id === item.id) : -1;
  const prev = navIndex > 0 ? navItems[navIndex - 1] : undefined;
  const next = navIndex >= 0 && navIndex < navItems.length - 1 ? navItems[navIndex + 1] : undefined;

  // Arrow keys step through images/videos in the current view — but not while a
  // text field (e.g. the Name input) has focus.
  useEffect(() => {
    if (navIndex < 0) return;
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA')) return;
      if (e.key === 'ArrowLeft' && prev) onNavigate(prev);
      else if (e.key === 'ArrowRight' && next) onNavigate(next);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navIndex, prev, next, onNavigate]);

  if (!item) return null;
  const badge = sourceBadge(item);
  const inboxPath = item.channel in INBOX_PATH ? INBOX_PATH[item.channel] : undefined;
  const canNavigate = navIndex >= 0 && navItems.length > 1;
  return (
    <Drawer opened={!!item} onClose={onClose} position="right" size="md" title="Media details">
      <Stack gap="md">
        <Box style={{ position: 'relative' }}>
          <MediaBubble kind={toBubbleKind(item.kind)} url={item.mediaUrl} alt={displayName(item)} />
          {canNavigate && (
            <>
              <ActionIcon
                variant="default"
                radius="xl"
                aria-label="Previous"
                disabled={!prev}
                onClick={() => prev && onNavigate(prev)}
                style={{ position: 'absolute', left: 6, top: '50%', transform: 'translateY(-50%)' }}
              >
                <IconChevronLeft size={18} />
              </ActionIcon>
              <ActionIcon
                variant="default"
                radius="xl"
                aria-label="Next"
                disabled={!next}
                onClick={() => next && onNavigate(next)}
                style={{
                  position: 'absolute',
                  right: 6,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <IconChevronRight size={18} />
              </ActionIcon>
            </>
          )}
        </Box>
        {canNavigate && (
          <Text size="xs" c="dimmed" ta="center">
            {navIndex + 1} / {navItems.length}
          </Text>
        )}
        <TextInput
          label="Name"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          rightSection={
            title.trim() && title.trim() !== displayName(item) ? (
              <ActionIcon
                variant="subtle"
                aria-label="Save name"
                onClick={() => onRename(item.id, title.trim())}
              >
                <IconUpload size={16} />
              </ActionIcon>
            ) : null
          }
        />
        <Stack gap={6}>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Source
            </Text>
            <Badge size="sm" variant="light" color={badge.color}>
              {badge.label}
            </Badge>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Type
            </Text>
            <Text size="sm">{item.mimeType ?? item.kind}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Size
            </Text>
            <Text size="sm">{formatBytes(item.fileSize)}</Text>
          </Group>
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              Added
            </Text>
            <Text size="sm">{new Date(item.createdAt).toLocaleString()}</Text>
          </Group>
        </Stack>
        {inboxPath && (
          <Anchor component={Link} to={inboxPath} size="sm">
            Open {badge.label} inbox →
          </Anchor>
        )}
        <Group justify="space-between" mt="sm">
          {item.mediaUrl ? (
            <Anchor
              href={item.mediaUrl}
              target="_blank"
              rel="noreferrer"
              download={item.fileName ?? true}
            >
              <Button variant="light" leftSection={<IconDownload size={16} />}>
                Download
              </Button>
            </Anchor>
          ) : (
            <span />
          )}
          <Button
            color="red"
            variant="light"
            leftSection={<IconTrash size={16} />}
            onClick={() => onDelete(item)}
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Drawer>
  );
}

function MediaPage() {
  const utils = trpc.useUtils();
  const [page, setPage] = useState(1);
  const [searchRaw, setSearchRaw] = useState('');
  const [search] = useDebouncedValue(searchRaw, 300);
  const [type, setType] = useTabParam<MediaType>('type', MEDIA_TYPES, 'all');
  const [channel, setChannel] = useState('');
  const [sort, setSort] = useState('createdAt:desc');
  // Most categories default to a thumbnail grid; documents default to a list
  // (with an opt-in grid of first-page previews). Audio is always a list.
  const [view, setView] = useLocalStorage<'grid' | 'list'>({
    key: 'leadsync.media.view',
    defaultValue: 'grid',
  });
  const [docView, setDocView] = useLocalStorage<'grid' | 'list'>({
    key: 'leadsync.media.view.docs',
    defaultValue: 'list',
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [detail, setDetail] = useState<MediaItem | null>(null);
  const [uploadOpen, uploadCtl] = useDisclosure(false);
  const [uploading, setUploading] = useState(false);
  const [confirm, setConfirm] = useState<{ ids: number[]; hasChat: boolean } | null>(null);

  const isDocs = type === 'documents';
  const showToggle = type !== 'audio';
  const effectiveView: 'grid' | 'list' = type === 'audio' ? 'list' : isDocs ? docView : view;
  const setEffectiveView = (v: 'grid' | 'list') => (isDocs ? setDocView(v) : setView(v));

  // Reset paging + selection whenever the result set changes shape.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [search, type, channel, sort]);

  const [sortField, sortDir] = useMemo(() => {
    const [f, d] = sort.split(':');
    return [f as 'createdAt' | 'fileSize' | 'title', d as 'asc' | 'desc'];
  }, [sort]);

  const kinds = TYPE_KINDS[type];
  const list = trpc.media.list.useQuery({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortDir,
    ...(search ? { search } : {}),
    ...(kinds ? { kinds } : {}),
    ...(channel ? { channel: channel as 'whatsapp' } : {}),
  });
  const usage = trpc.media.usage.useQuery();

  const items = useMemo<MediaItem[]>(() => list.data?.items ?? [], [list.data]);
  const navItems = useMemo(() => items.filter((i) => NAV_KINDS.has(i.kind)), [items]);
  const totalPages = list.data
    ? Math.max(1, Math.ceil(list.data.totalCount / list.data.pageSize))
    : 1;

  const pageIds = useMemo(() => items.map((i) => i.id), [items]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selected.has(id));
  const somePageSelected = pageIds.some((id) => selected.has(id));

  const invalidate = () => {
    void utils.media.list.invalidate();
    void utils.media.usage.invalidate();
  };

  const uploadMut = trpc.media.upload.useMutation();
  const renameMut = trpc.media.rename.useMutation({
    onSuccess: () => {
      invalidate();
      notifications.show({ message: 'Renamed', color: 'green' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const deleteMut = trpc.media.delete.useMutation({
    onSuccess: (r) => {
      invalidate();
      setDetail(null);
      notifications.show({
        message: r.hardDeleted ? 'File deleted' : 'Hidden from library',
        color: 'green',
      });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const bulkMut = trpc.media.bulkDelete.useMutation({
    onSuccess: (r) => {
      invalidate();
      setSelected(new Set());
      notifications.show({ message: `Removed ${r.deleted} file(s)`, color: 'green' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const onRename = (id: number, title: string) => renameMut.mutate({ id, title });

  async function handleDrop(files: File[]) {
    setUploading(true);
    let ok = 0;
    for (const file of files) {
      if (file.size > MAX_BYTES) {
        notifications.show({ message: `${file.name} exceeds 30MB`, color: 'red' });
        continue;
      }
      try {
        const bytesBase64 = await fileToBase64(file);
        await uploadMut.mutateAsync({
          filename: file.name,
          mimeType: file.type || 'application/octet-stream',
          bytesBase64,
        });
        ok++;
      } catch (err) {
        notifications.show({
          message: `Failed to upload ${file.name}: ${(err as Error).message}`,
          color: 'red',
        });
      }
    }
    setUploading(false);
    if (ok > 0) {
      invalidate();
      notifications.show({ message: `Uploaded ${ok} file(s)`, color: 'green' });
      uploadCtl.close();
    }
  }

  function toggle(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllPage() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  }

  function askDelete(ids: number[]) {
    const set = new Set(ids);
    const hasChat = items.some((i) => set.has(i.id) && i.source !== 'library_upload');
    setConfirm({ ids, hasChat });
  }

  function runConfirmedDelete() {
    if (!confirm) return;
    if (confirm.ids.length === 1) deleteMut.mutate({ id: confirm.ids[0]! });
    else bulkMut.mutate({ ids: confirm.ids });
    setConfirm(null);
  }

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={2}>Media files</Title>
            <Text size="sm" c="dimmed">
              {usage.data
                ? `${usage.data.totalCount} files · ${formatBytes(usage.data.totalBytes)} stored`
                : 'Your media across every channel, in one place.'}
            </Text>
          </Box>
          <Button leftSection={<IconUpload size={16} />} onClick={uploadCtl.open}>
            Upload
          </Button>
        </Group>

        <Tabs value={type} onChange={(v) => setType((v as MediaType) ?? 'all')} keepMounted={false}>
          <Tabs.List>
            {TAB_META.map(({ value, label, Icon }) => {
              const count = countForType(value, usage.data);
              return (
                <Tabs.Tab
                  key={value}
                  value={value}
                  leftSection={<Icon size={14} />}
                  rightSection={
                    count != null ? (
                      <Badge size="xs" variant="light" circle>
                        {count}
                      </Badge>
                    ) : null
                  }
                >
                  {label}
                </Tabs.Tab>
              );
            })}
          </Tabs.List>
        </Tabs>

        <Card p="sm" withBorder>
          <Group gap="xs" wrap="wrap">
            <TextInput
              flex={1}
              miw={200}
              leftSection={<IconSearch size={16} />}
              placeholder="Search by name…"
              value={searchRaw}
              onChange={(e) => setSearchRaw(e.currentTarget.value)}
            />
            <Select
              w={160}
              data={CHANNEL_OPTIONS}
              value={channel}
              onChange={(v) => setChannel(v ?? '')}
              allowDeselect={false}
            />
            <Select
              w={160}
              data={SORT_OPTIONS}
              value={sort}
              onChange={(v) => setSort(v ?? 'createdAt:desc')}
              allowDeselect={false}
            />
            {showToggle && (
              <SegmentedControl
                value={effectiveView}
                onChange={(v) => setEffectiveView(v as 'grid' | 'list')}
                data={[
                  { value: 'grid', label: <IconLayoutGrid size={16} /> },
                  { value: 'list', label: <IconList size={16} /> },
                ]}
              />
            )}
          </Group>
        </Card>

        {items.length > 0 && (
          <Group justify="space-between">
            <Checkbox
              checked={allPageSelected}
              indeterminate={somePageSelected && !allPageSelected}
              onChange={toggleSelectAllPage}
              label={allPageSelected ? 'Deselect all' : 'Select all'}
            />
            {selected.size > 0 && (
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  {selected.size} selected
                </Text>
                <Button variant="subtle" size="xs" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button
                  color="red"
                  size="xs"
                  leftSection={<IconTrash size={14} />}
                  onClick={() => askDelete([...selected])}
                >
                  Delete ({selected.size})
                </Button>
              </Group>
            )}
          </Group>
        )}

        {list.isPending ? (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }}>
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} height={190} radius="md" />
            ))}
          </SimpleGrid>
        ) : list.isError ? (
          <Text c="red">{list.error.message}</Text>
        ) : items.length === 0 ? (
          <Paper p="xl" withBorder ta="center">
            <IconPhoto size={40} opacity={0.4} />
            <Text c="dimmed" mt="sm">
              No media yet. Upload a file, or media will appear here as you chat.
            </Text>
          </Paper>
        ) : effectiveView === 'grid' ? (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4, lg: 5 }}>
            {items.map((item) => (
              <MediaTile
                key={item.id}
                item={item}
                selected={selected.has(item.id)}
                onToggle={() => toggle(item.id)}
                onOpen={() => setDetail(item)}
                onRename={onRename}
              />
            ))}
          </SimpleGrid>
        ) : (
          <Card p={0} withBorder>
            <Table highlightOnHover withRowBorders={false} verticalSpacing="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={36}>
                    <Checkbox
                      checked={allPageSelected}
                      indeterminate={somePageSelected && !allPageSelected}
                      onChange={toggleSelectAllPage}
                      aria-label="Select all on page"
                    />
                  </Table.Th>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Size</Table.Th>
                  <Table.Th>Added</Table.Th>
                  <Table.Th w={40} />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item) => {
                  const badge = sourceBadge(item);
                  return (
                    <Table.Tr key={item.id} style={{ cursor: 'pointer' }}>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selected.has(item.id)}
                          onChange={() => toggle(item.id)}
                          aria-label="Select"
                        />
                      </Table.Td>
                      <Table.Td>
                        <Box maw={260}>
                          <EditableName item={item} onRename={onRename} />
                        </Box>
                      </Table.Td>
                      <Table.Td onClick={() => setDetail(item)}>
                        <Badge size="xs" variant="light" color={badge.color}>
                          {badge.label}
                        </Badge>
                      </Table.Td>
                      <Table.Td onClick={() => setDetail(item)}>
                        <Text size="xs" c="dimmed">
                          {item.kind}
                        </Text>
                      </Table.Td>
                      <Table.Td onClick={() => setDetail(item)}>
                        <Text size="xs">{formatBytes(item.fileSize)}</Text>
                      </Table.Td>
                      <Table.Td onClick={() => setDetail(item)}>
                        <Text size="xs" c="dimmed">
                          {new Date(item.createdAt).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => askDelete([item.id])}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Card>
        )}

        {list.data && list.data.totalCount > PAGE_SIZE && (
          <Group justify="space-between">
            <Text size="sm" c="dimmed">
              {list.data.totalCount} files
            </Text>
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Stack>

      <DetailDrawer
        item={detail}
        navItems={navItems}
        onNavigate={setDetail}
        onClose={() => setDetail(null)}
        onRename={onRename}
        onDelete={(it) => askDelete([it.id])}
      />

      <Modal opened={uploadOpen} onClose={uploadCtl.close} title="Upload media" centered>
        <Dropzone
          onDrop={handleDrop}
          loading={uploading}
          maxSize={MAX_BYTES}
          accept={['image/*', 'video/*', 'audio/*', 'application/pdf']}
        >
          <Stack
            align="center"
            justify="center"
            mih={140}
            gap="xs"
            style={{ pointerEvents: 'none' }}
          >
            <Dropzone.Accept>
              <IconUpload size={40} />
            </Dropzone.Accept>
            <Dropzone.Reject>
              <IconX size={40} />
            </Dropzone.Reject>
            <Dropzone.Idle>
              <IconPhoto size={40} opacity={0.5} />
            </Dropzone.Idle>
            <Text size="sm">Drag files here or click to select</Text>
            <Text size="xs" c="dimmed">
              Images, video, audio, PDF · up to 30MB each
            </Text>
          </Stack>
        </Dropzone>
      </Modal>

      <Modal opened={!!confirm} onClose={() => setConfirm(null)} title="Delete media?" centered>
        <Stack>
          <Text size="sm">
            {confirm?.hasChat
              ? 'Files that came from a conversation will be hidden from the library — the original chat keeps them. Files you uploaded to the library will be permanently deleted.'
              : 'These files will be permanently deleted. This cannot be undone.'}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirm(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              loading={deleteMut.isPending || bulkMut.isPending}
              onClick={runConfirmedDelete}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </motion.div>
  );
}

function MediaRoute() {
  return (
    <PermissionGate permission="messaging">
      <MediaPage />
    </PermissionGate>
  );
}

export const Route = createFileRoute('/vendor/media/')({
  component: MediaRoute,
  validateSearch: (s: Record<string, unknown>): { type?: MediaType } => {
    const t = s.type;
    return typeof t === 'string' && (MEDIA_TYPES as readonly string[]).includes(t)
      ? { type: t as MediaType }
      : {};
  },
});
